import { ColliWorkflowClient } from '@frontend/colli/api';
import { Colli, ColliTriggerName } from '@frontend/colli/types';
import { SelectOption } from '@frontend/components/basic-forms';
import { useModals } from '@frontend/modal';
import { PossibleTrigger } from '@frontend/workflow/types';
import { useEffect, useState } from 'react';

import { groupPossibleTriggerOptions } from '../../../workflow/possible-trigger-labels';
import { getColliPossibleTriggerDisplay } from './colli-possible-trigger-display';

export interface ChangeColliStateModalProps {
    collis: Colli[];
    onSubmitCallback?: () => void;
}

export const useChangeColliStateModal = (props: ChangeColliStateModalProps) => {
    const { closeTopModal } = useModals();
    const [selectedTrigger, setSelectedTrigger] = useState<ColliTriggerName | undefined>(undefined);
    const [options, setOptions] = useState<SelectOption<ColliTriggerName>[]>([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        setIsLoading(true);
        setSelectedTrigger(undefined);
        loadColliTriggerOptions(props.collis)
            .then(setOptions)
            .finally(() => setIsLoading(false));
    }, [props.collis]);

    return { options, selectedTrigger, setSelectedTrigger, closeTopModal, isLoading };
};

export async function loadColliTriggerOptions(collis: Colli[]): Promise<SelectOption<ColliTriggerName>[]> {
    const colli = collis[0];
    if (!colli) return [];

    try {
        const triggers = await ColliWorkflowClient.fetchColliPossibleTriggers(colli.account_id, colli.transaction_id, colli.id);
        return mapColliTriggerOptions(triggers);
    } catch {
        return [];
    }
}

export function mapColliTriggerOptions(triggers: PossibleTrigger<ColliTriggerName, string | null>[]): SelectOption<ColliTriggerName>[] {
    return groupPossibleTriggerOptions(
        triggers.map((possibleTrigger) => {
            const { group, display } = getColliPossibleTriggerDisplay(possibleTrigger);
            return {
                group,
                option: {
                    display,
                    label: possibleTrigger.trigger,
                    value: possibleTrigger.trigger
                }
            };
        })
    );
}
